import { z } from "zod";

/* ===========================================================================
 * US-E9.F2-0058 Payment Reminder config (@UC0070).
 * Key `reminder_days_before` (SystemSetting/ConfigOverride) — số ngày trước hạn để nhắc đóng quỹ.
 * Default 3 (PO chốt 01/07). Config per-fund (groupOverridable).
 * ========================================================================= */

export const REMINDER_DAYS_BEFORE_KEY = "reminder_days_before";

/** Số ngày trước hạn [0, 30]. Số nguyên. */
export const reminderDaysBeforeSchema = z.number().int().min(0).max(30);

export const REMINDER_DAYS_BEFORE_DEFAULT = 3;

/* ---- US-E9.F2-0059 overdue-alert ---- */
/** Cờ nhắc quá hạn hàng ngày (true) hay 1 lần (false — default). Per-fund. */
export const OVERDUE_DAILY_REMINDER_KEY = "overdue_daily_reminder";
export const overdueDailyReminderSchema = z.boolean();
export const OVERDUE_DAILY_REMINDER_DEFAULT = false;

/* ===========================================================================
 * US-E7.F2-0080 Notification Preferences (@UC0008) — GET/PUT /v1/me/notification-preferences.
 *
 * Shape KHỚP domain resolver (@goppy/domain notification-preference): category (nhóm event) ×
 * channel (in_app|push). Preference CẤP USER (không per-group MVP). Enum literal ĐỒNG BỘ với domain
 * — validate biên (C5) fail-closed: category/channel lạ → 400.
 *
 * INVARIANTS:
 *  - in_app never-off: item in_app trả về LUÔN enabled=true (backend áp); PUT tắt in_app bỏ qua.
 *  - KHÔNG sms (C1): channel chỉ in_app | push.
 *  - .strict() chặn field lạ ở PUT body.
 * ========================================================================= */

export const NOTIFICATION_PREFERENCE_CATEGORIES = [
  // 🔴 `consensus` ĐÃ GỠ 23/08/2026 (US-E12.F1-0512) — phải khớp `NOTIFICATION_CATEGORIES` ở
  // `@goppy/domain`; lý do đầy đủ ghi ở đó. Gỡ một bên mà quên bên kia ⇒
  // `notification-category-parity.spec.ts` ĐỎ (đó là lý do nó tồn tại).
  "payment_reminder",
  "payment_confirmed",
  "collection",
  "expense",
  // 🔴 US-E24.F53-0510 (QĐ người dùng 20/08) — nhóm THỨ SÁU, phải khớp `NOTIFICATION_CATEGORIES` ở
  // `@goppy/domain`. Thêm một bên mà quên bên kia ⇒ `notification-category-parity.spec.ts` đỏ (đó là
  // lý do nó tồn tại). Xem docblock ở domain để biết vì sao nhóm này sinh ra.
  "system",
] as const;

export const NOTIFICATION_PREFERENCE_CHANNELS = ["in_app", "push"] as const;

export const notificationPreferenceCategorySchema = z.enum(
  NOTIFICATION_PREFERENCE_CATEGORIES,
);
export const notificationPreferenceChannelSchema = z.enum(
  NOTIFICATION_PREFERENCE_CHANNELS,
);

/** Một ô preference: (category, channel) → enabled. */
export const notificationPreferenceItemSchema = z
  .object({
    category: notificationPreferenceCategorySchema,
    channel: notificationPreferenceChannelSchema,
    enabled: z.boolean(),
  })
  .strict();

export type NotificationPreferenceItem = z.infer<typeof notificationPreferenceItemSchema>;

/** Response GET/PUT — danh sách đầy đủ mọi (category, channel) đã resolve (default ON). */
export const notificationPreferencesResponseSchema = z.object({
  preferences: z.array(notificationPreferenceItemSchema),
});

export type NotificationPreferencesResponse = z.infer<
  typeof notificationPreferencesResponseSchema
>;

/**
 * Body PUT — danh sách ô cần cập nhật. category/channel BẮT BUỘC ∈ enum (z.enum → 400 nếu lạ).
 * in_app đưa vào cũng bị backend bỏ qua (never-off). .strict() chặn field lạ tại biên.
 */
export const updateNotificationPreferencesBodySchema = z
  .object({
    preferences: z.array(notificationPreferenceItemSchema).max(50),
  })
  .strict();

export type UpdateNotificationPreferencesBody = z.infer<
  typeof updateNotificationPreferencesBodySchema
>;
